import { microToMilli as microsecondsToMillisecond } from '@core/core/constants'

import type { SegyHeaderType } from '../../../../../types'
import type {
  BinaryHeaderRowTemplateType,
  FormatMappingKeys,
  TraceHeaderRowTemplateType,
} from './types'

export const IBM037Mapping = new Map<number, string>([
  [0x25, ''],
  [0x40, ' '],
  [0x5a, '!'],
  [0x7f, '"'],
  [0x7b, '#'],
  [0x5b, '$'],
  [0x6c, '%'],
  [0x50, '&'],
  [0x7d, '"\'"'],
  [0x4d, '('],
  [0x5d, ')'],
  [0x5c, '*'],
  [0x4e, '+'],
  [0x6b, '],'],
  [0x60, '-'],
  [0x4b, '.'],
  [0x61, '/'],
  [0xf0, '0'],
  [0xf1, '1'],
  [0xf2, '2'],
  [0xf3, '3'],
  [0xf4, '4'],
  [0xf5, '5'],
  [0xf6, '6'],
  [0xf7, '7'],
  [0xf8, '8'],
  [0xf9, '9'],
  [0x7a, ':'],
  [0x5e, ';'],
  [0x4c, '<'],
  [0x7e, '='],
  [0x6e, '>'],
  [0x6f, '?'],
  [0x7c, '@'],
  [0xc1, 'A'],
  [0xc2, 'B'],
  [0xc3, 'C'],
  [0xc4, 'D'],
  [0xc5, 'E'],
  [0xc6, 'F'],
  [0xc7, 'G'],
  [0xc8, 'H'],
  [0xc9, 'I'],
  [0xd1, 'J'],
  [0xd2, 'K'],
  [0xd3, 'L'],
  [0xd4, 'M'],
  [0xd5, 'N'],
  [0xd6, 'O'],
  [0xd7, 'P'],
  [0xd8, 'Q'],
  [0xd9, 'R'],
  [0xe2, 'S'],
  [0xe3, 'T'],
  [0xe4, 'U'],
  [0xe5, 'V'],
  [0xe6, 'W'],
  [0xe7, 'X'],
  [0xe8, 'Y'],
  [0xe9, 'Z'],
  [0xba, '['],
  [0xe0, '\\'],
  [0xbb, ']'],
  [0xb0, '^'],
  [0x6d, '_'],
  [0x79, '`'],
  [0x81, 'a'],
  [0x82, 'b'],
  [0x83, 'c'],
  [0x84, 'd'],
  [0x85, 'e'],
  [0x86, 'f'],
  [0x87, 'g'],
  [0x88, 'h'],
  [0x89, 'i'],
  [0x91, 'j'],
  [0x92, 'k'],
  [0x93, 'l'],
  [0x94, 'm'],
  [0x95, 'n'],
  [0x96, 'o'],
  [0x97, 'p'],
  [0x98, 'q'],
  [0x99, 'r'],
  [0xa2, 's'],
  [0xa3, 't'],
  [0xa4, 'u'],
  [0xa5, 'v'],
  [0xa6, 'w'],
  [0xa7, 'x'],
  [0xa8, 'y'],
  [0xa9, 'z'],
  [0xc0, '{'],
  [0x4f, '|'],
  [0xd0, '}'],
])

const convertMicrosecondsToMilliseconds = (parseResult: number): number =>
  parseResult / microsecondsToMillisecond

export const binaryHeaderTemplate: BinaryHeaderRowTemplateType[] = [
  { position: 0, type: 'uint32', description: 'TraceSequJob identification number.enceLine' },
  { position: 4, type: 'uint32', description: 'Line number.' },
  { position: 8, type: 'uint32', description: 'Reel number.' },
  { position: 12, type: 'uint16', description: 'Number of data traces per record.' },
  { position: 14, type: 'uint16', description: 'Number of auxiliary traces per record.' },
  {
    position: 16,
    type: 'uint16',
    description: 'Sample interval, milliseconds, this file (reel).',
    postProcess: convertMicrosecondsToMilliseconds,
  },
  {
    position: 18,
    type: 'uint16',
    description: 'Sample interval, milliseconds, original field recording.',
    postProcess: convertMicrosecondsToMilliseconds,
  },
  {
    position: 20,
    type: 'uint16',
    description: 'Number of samples per data trace, this file (reel).',
  },
  {
    position: 22,
    type: 'uint16',
    description: 'Number of samples per data trace, original field recording.',
  },
  {
    position: 24,
    type: 'uint16',
    description:
      'Data sample format code: 1 = floating point (4 bytes) 2 = fixed point (4 bytes) 3 = fixed point (2 bytes) 4 = fixed point with gain code (4 bytes). 5 = IEEE floating-point (4 bytes)',
  },
  { position: 26, type: 'uint16', description: 'CDP fold.' },
  {
    position: 28,
    type: 'uint16',
    description:
      'Trace sorting code:  0 = Unknown 1 = as recorded (no) sorting) 2 = CDP ensemble 3 = single fold continuous profile 4 = horizontally stacked',
  },
  {
    position: 30,
    type: 'uint16',
    description: 'Vertical sum code: 1 = no sum 2 = two sum ... N = N sum (N = 32,767)',
  },
  { position: 32, type: 'uint16', description: 'Sweep frequency at start (Hz).' },
  { position: 34, type: 'uint16', description: 'Sweep frequency at end (Hz).' },
  { position: 36, type: 'uint16', description: 'Sweep length, ms.' },
  {
    position: 38,
    type: 'uint16',
    description: 'Sweep type code: 1 = linear 2 = parabolic 3 = exponential 4 = other',
  },
  { position: 40, type: 'uint16', description: 'Trace number of sweep channel.' },
  {
    position: 42,
    type: 'uint16',
    description: 'Sweep trace taper length, ms, at start if tapered.',
  },
  { position: 44, type: 'uint16', description: 'Sweep trace taper length, ms, at end.' },
  { position: 46, type: 'uint16', description: 'Taper type: 1 = linear 2 = cos 3 = other' },
  { position: 48, type: 'uint16', description: 'Correlated data traces: 1 = no 2 = yes' },
  { position: 50, type: 'uint16', description: 'Binary gain recovered: 1 = yes 2 = no' },
  {
    position: 52,
    type: 'uint16',
    description: 'Amplitude recovery method: 1 = none 2 = spherical divergence 3 = AGC 4 = other',
  },
  { position: 54, type: 'uint16', description: 'Measurement system: 1 = meters 2 = feet' },
  {
    position: 56,
    type: 'uint16',
    description: 'Impulse signal: 1 = Upward = negative number. 2 = Upward = positive number.',
  },
  {
    position: 58,
    type: 'uint16',
    description:
      'Vibratory polarity code - seismic signal lags pilot signal by: 1 = 337.5 - 22.5 degrees 2 = 22.5 - 67.5 degrees 3 = 67.5 - 112.5 degrees 4 = 112.5 - 157.5 degrees 5 = 157.5 - 202.5 degrees 6 = 202.5 - 247.5 degrees',
  },
]

export const DataSampleFormatCodeMapping = new Map([
  [1, 4], //byte IBM floating-point
  [2, 4], //byte, two's complement integer
  [3, 2], //byte, two's complement integer
  [4, 4], //byte fixed-point with gain (obsolete)
  [5, 4], //byte IEEE floating-point
  [6, 8], //byte IEEE floating-point
  [7, 3], //byte two’s complement integer
  [8, 1], //byte, two's complement integer
  [9, 8], //byte, two's complement integer
  [10, 4], //byte, unsigned integer
  [11, 2], //byte, unsigned integer
  [12, 8], //byte, unsigned integer
  [15, 3], //byte, unsigned integer
  [16, 1], //byte, unsigned integer
])

export const traceHeaderTemplate: TraceHeaderRowTemplateType[] = [
  { position: 0, type: 'int32', description: 'TraceSequenceLine' },
  { position: 4, type: 'int32', description: 'TraceSequenceFile' },
  { position: 8, type: 'int32', description: 'FieldRecord' },
  { position: 12, type: 'int32', description: 'TraceNumber' },
  { position: 16, type: 'int32', description: 'EnergySourcePoint' },
  { position: 20, type: 'int32', description: 'cdp' },
  { position: 24, type: 'int32', description: 'cdpTrace' },
  { position: 28, type: 'int16', description: 'TraceIdenitifactionCode' },
  { position: 30, type: 'int16', description: 'NSummedTraces' },
  { position: 32, type: 'int16', description: 'NStackedTraces' },
  { position: 34, type: 'int16', description: 'DataUse' },
  { position: 36, type: 'int32', description: 'offset' },
  { position: 40, type: 'int32', description: 'ReceiverGroupElevation' },
  { position: 44, type: 'int32', description: 'SourceSurfaceElevation' },
  { position: 48, type: 'int32', description: 'SourceDepth' },
  { position: 52, type: 'int32', description: 'ReceiverDatumElevation' },
  { position: 56, type: 'int32', description: 'SourceDatumElevation' },
  { position: 60, type: 'int32', description: 'SourceWaterDepth' },
  { position: 64, type: 'int32', description: 'GroupWaterDepth' },
  { position: 68, type: 'int16', description: 'ElevationScalar' },
  { position: 70, type: 'int16', description: 'SourceGroupScalar' },
  { position: 72, type: 'int32', description: 'SourceX' },
  { position: 76, type: 'int32', description: 'SourceY' },
  { position: 80, type: 'int32', description: 'GroupX' },
  { position: 84, type: 'int32', description: 'GroupY' },
  { position: 88, type: 'int16', description: 'CoordinateUnits' },
  { position: 90, type: 'int16', description: 'WeatheringVelocity' },
  { position: 92, type: 'int16', description: 'SubWeatheringVelocity' },
  { position: 94, type: 'int16', description: 'SourceUpholeTime' },
  { position: 96, type: 'int16', description: 'GroupUpholeTime' },
  { position: 98, type: 'int16', description: 'SourceStaticCorrection' },
  { position: 100, type: 'int16', description: 'GroupStaticCorrection' },
  { position: 102, type: 'int16', description: 'TotalStaticApplied' },
  { position: 104, type: 'int16', description: 'LagTimeA' },
  { position: 106, type: 'int16', description: 'LagTimeB' },
  { position: 108, type: 'int16', description: 'DelayRecordingTime' },
  { position: 110, type: 'int16', description: 'MuteTimeStart' },
  { position: 112, type: 'int16', description: 'MuteTimeEND' },
  { position: 114, type: 'uint16', description: 'ns' },
  { position: 116, type: 'uint16', description: 'dt' },
  { position: 118, type: 'int16', description: 'GainType' },
  { position: 120, type: 'int16', description: 'InstrumentGainConstant' },
  { position: 122, type: 'int16', description: 'InstrumentInitialGain' },
  { position: 124, type: 'int16', description: 'Correlated' },
  { position: 126, type: 'int16', description: 'SweepFrequenceStart' },
  { position: 128, type: 'int16', description: 'SweepFrequenceEnd' },
  { position: 130, type: 'int16', description: 'SweepLength' },
  { position: 132, type: 'int16', description: 'SweepType' },
  { position: 134, type: 'int16', description: 'SweepTraceTaperLengthStart' },
  { position: 136, type: 'int16', description: 'SweepTraceTaperLengthEnd' },
  { position: 138, type: 'int16', description: 'TaperType' },
  { position: 140, type: 'int16', description: 'AliasFilterFrequency' },
  { position: 142, type: 'int16', description: 'AliasFilterSlope' },
  { position: 144, type: 'int16', description: 'NotchFilterFrequency' },
  { position: 146, type: 'int16', description: 'NotchFilterSlope' },
  { position: 148, type: 'int16', description: 'LowCutFrequency' },
  { position: 150, type: 'int16', description: 'HighCutFrequency' },
  { position: 152, type: 'int16', description: 'LowCutSlope' },
  { position: 154, type: 'int16', description: 'HighCutSlope' },
  { position: 156, type: 'int16', description: 'YearDataRecorded' },
  { position: 158, type: 'int16', description: 'DayOfYear' },
  { position: 160, type: 'int16', description: 'HourOfDay' },
  { position: 162, type: 'int16', description: 'MinuteOfHour' },
  { position: 164, type: 'int16', description: 'SecondOfMinute' },
  { position: 166, type: 'int16', description: 'TimeBaseCode' },
  { position: 168, type: 'int16', description: 'TraceWeightningFactor' },
  { position: 170, type: 'int16', description: 'GeophoneGroupNumberRoll1' },
  { position: 172, type: 'int16', description: 'GeophoneGroupNumberFirstTraceOrigField' },
  { position: 174, type: 'int16', description: 'GeophoneGroupNumberLastTraceOrigField' },
  { position: 176, type: 'int16', description: 'GapSize' },
  { position: 178, type: 'int16', description: 'OverTravel' },
  { position: 180, type: 'int32', description: 'cdpX' },
  { position: 184, type: 'int32', description: 'cdpY' },
  { position: 188, type: 'int32', description: 'Inline3D' },
  { position: 192, type: 'int32', description: 'Crossline3D' },
  { position: 196, type: 'int32', description: 'ShotPoint' },
  { position: 200, type: 'int16', description: 'ShotPointScalar' },
  { position: 202, type: 'int16', description: 'TraceValueMeasurementUnit' },
  { position: 204, type: 'int32', description: 'TransductionConstantMantissa' },
  { position: 208, type: 'int16', description: 'TransductionConstantPower' },
  { position: 210, type: 'int16', description: 'TransductionUnit' },
  { position: 212, type: 'int16', description: 'TraceIdentifier' },
  { position: 214, type: 'int16', description: 'ScalarTraceHeader' },
  { position: 216, type: 'int16', description: 'SourceType' },
  { position: 218, type: 'int32', description: 'SourceEnergyDirectionMantissa' },
  { position: 222, type: 'int16', description: 'SourceEnergyDirectionExponent' },
  { position: 224, type: 'int32', description: 'SourceMeasurementMantissa' },
  { position: 228, type: 'int16', description: 'SourceMeasurementExponent' },
  { position: 230, type: 'int16', description: 'SourceMeasurementUnit' },
  { position: 232, type: 'int32', description: 'UnassignedInt1' },
  { position: 236, type: 'int32', description: 'UnassignedInt2' },
]

export const FormatMapping: Record<FormatMappingKeys, SegyHeaderType> = {
  1: 'IBM_FLOATING_POINT',
  2: 'INT_4_BYTES',
  3: 'INT_2_BYTES',
  5: 'IEEE_FLOATING_POINT',
}
